import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  inject,
  Injector,
  input,
  Input,
  ViewChild,
} from '@angular/core';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GetContextMenuItemsParams,
  MenuItemDef,
} from '@ag-grid-community/core';
import { EMPTY, Observable, first, map, of } from 'rxjs';
import {
  BaseDocDto,
  DocLineDeletedDto,
  DocLineMovedDto,
  DocLineRole,
  DocLineUpdatedDto,
  SupplierDeliveryNoteDto,
  SupplierDeliveryNoteService,
  SupplierDeliveryStatus,
  SupplierPurchaseOrderLookup,
} from '@digiwall/goliath-documents/proxy';
import {
  BaseThirdPartyDocLineEditor,
  IStockDocLineEditor,
  LinesEditorComponent,
  StockDocLineEditorHelperService,
} from '../../../../components';
import { VatType } from '@digiwall/goliath-core/proxy';
import { GoliathCoreEnumerationTypeStore } from '@digiwall/goliath-core';
import {
  FieldTypes,
  ITypeAheadCellEditorParams,
  TypeAheadCellEditorComponent,
} from '@digiwall/ag-grid';
import { ObserverQueue, ShortcutKey } from '@digiwall/core';
import { DocType } from '../../../base-docs/detail/doc-type';
import { LabelPrinterHelper } from '@digiwall/goliath-products';
import { Router } from '@angular/router';
import { StockLinePolicy } from '../../../stock-docs/stock-doc-movements/stock-doc-movement-detail/stock-doc-movement-lines/stock-doc-movement-lines-policies/stock-doc-movement-line.policy';
import { SupplierDeliveryNoteEntryMovementPolicy } from './supplier-delivery-note-lines-policies/supplier-delivery-note-entry-movement-line.policy';
import { SupplierDeliveryNoteMovementPolicy } from './supplier-delivery-note-lines-policies/supplier-delivery-note-movement-line.policy';

@Component({
  selector: 'document-supplier-delivery-note-lines-grid',
  templateUrl: './supplier-delivery-note-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [StockDocLineEditorHelperService, ObserverQueue],
})
export class SupplierDeliveryNoteLinesEditorComponent
  extends BaseThirdPartyDocLineEditor<SupplierDeliveryNoteDto>
  implements
    IStockDocLineEditor<DocLineUpdatedDto, DocLineMovedDto, DocLineDeletedDto>,
    AfterViewInit
{
  coreEnumerationTypeStore = inject(GoliathCoreEnumerationTypeStore);

  public readonly isSerialEntry = input.required<boolean>();
  public readonly copyLineData = input.required<boolean>();
  public readonly printLabel = input.required<boolean>();
  public readonly selectedPurchaseOrderSelectedIds = input.required<string[]>();

  private labelPrinterHelper = inject(LabelPrinterHelper);

  private _policy!: StockLinePolicy;

  private get policy(): StockLinePolicy {
    return this._policy;
  }

  public get stockLocalizationService() {
    return this.localizationService;
  }
  public get stockArticleService() {
    return this.articleService;
  }
  public get stockLabelPrinterHelper() {
    return this.labelPrinterHelper;
  }
  public get stockColDefs(): ColDef[] {
    return this.columnDefs;
  }
  public stockIsBusyAdding(value: boolean): void {
    this._isBusyAdding.set(value);
  }

  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  public get document() {
    return this._document;
  }

  public get Readonly() {
    return this._readonly;
  }

  get stockDocService() {
    return {
      ...(this._docService as SupplierDeliveryNoteService),
      setLineLocationId: () => {
        throw new Error('setLineLocationId is not implemented for SupplierDeliveryNoteService');
      },
    };
  }
  get updateLines(): any {
    return this._updateLines;
  }

  public get editorHelper() {
    return this._editorHelper;
  }

  @Input() set supplierDeliveryNote(value: SupplierDeliveryNoteDto) {
    this._document = value;
    this.setLanguageCode();
  }

  @Input() set readonly(value: boolean) {
    this._readonly = value;
  }

  protected override handlingLinesUpdatedEvent = true;

  protected articleLineRole: DocLineRole = DocLineRole.SupplierDeliveryNoteArticle;
  protected textLineRole: DocLineRole = DocLineRole.DocText;
  protected chapterLineRole: DocLineRole = DocLineRole.DocChapter;
  protected SupplierDeliveryStatus = SupplierDeliveryStatus;

  protected override articleLookupFn = (term: string) =>
    this.articleService.getArticlePurchasableSelectionLookup(term);

  public columnDefs: Array<ColDef> = [];

  private lineGridApi = computed(() => this.lineGrid?.grid?.grid?.api);

  constructor(
    localizationService: LocalizationService,
    protected service: SupplierDeliveryNoteService,
    injector: Injector,
    private _editorHelper: StockDocLineEditorHelperService<
      DocLineUpdatedDto,
      DocLineMovedDto,
      DocLineDeletedDto
    >,
    public updateQueue: ObserverQueue,
  ) {
    super(localizationService, service, injector, updateQueue);
    _editorHelper.DocLineEditor = this;

    this.setLinePolicy();
    this.initColDefs();
  }

  private setLinePolicy(): void {
    if (!this._policy) {
      const router = inject(Router);
      const isEntry = router.url.split('/').includes('entry');
      if (isEntry) {
        this._policy = new SupplierDeliveryNoteEntryMovementPolicy(this);

        const parentBtns = this.addActionBtns;
        this.addActionBtns = computed(() => {
          const btns = parentBtns();
          btns
            .filter(btn => btn.key == 'ADD_ARTICLE_LINE')
            .forEach(btn => {
              btn.shortcutKey = ShortcutKey.controlAdd;
            });
          return btns;
        });
      } else {
        this._policy = new SupplierDeliveryNoteMovementPolicy(this);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.currentCompanyStore.currentCompany() != null && this.lineGridApi() != null) {
      this.lineGridApi()?.redrawRows();
    }
  }

  private initColDefs(): void {
    let columnDefs = super.colDefs();
    columnDefs.push(
      ...([
        {
          field: 'salesPurchaseDisplayNumber',
          width: 120,
          headerName: this.localizationService.instant(
            'Doc::SupplierDeliveryNote:SalesPurchaseDisplayNumber',
          ),
          type: FieldTypes.String,
          suppressNavigable: true,
        },
        this.colDef_Quantity,
        this.colDef_ArticleSalesUnitCode(DocType.Purchase),
        this.colDef_UnitPrice,
        ...this.getColDef_StockColumns(),
        this.colDef_Discount,
        this.colDef_VatCode(VatType.Sales),
        this.colDef_TotalPrice,
        this.colDef_UnitWeight,
        this._editorHelper.colDef_StockLocation,
        this.colDef_TotalReceivedQuantity,
        {
          field: 'orderedQuantity',
          width: 120,
          headerName: this.localizationService.instant('Doc::SupplierDeliveryNote:OrderedQuantity'),
          type: FieldTypes.Number,
          cellRendererParams: {
            decimals: this.currentCompanyStore.quantityDecimals,
          },
          suppressNavigable: true,

          editable: params => this.isCellEditable(params),
        },
        this.colDef_RemainingQuantityToReceive,
        ...(this.stockFeature.isEnabled
          ? [
              this.getColDef_TotalReceivedQuantityStock(),
              {
                field: 'orderedQuantityInStockUnit',
                width: 120,
                headerName: this.localizationService.instant(
                  'Doc::SupplierDeliveryNote:OrderedQuantityStock',
                ),
                type: FieldTypes.Number,
                cellRendererParams: {
                  decimals: this.currentCompanyStore.quantityDecimals(),
                },
                suppressNavigable: true,

                editable: params => this.isCellEditable(params),
              },
              this.getColDef_RemainingQuantityToReceiveStock(),
            ]
          : []),
        {
          field: 'supplierDiscount',
          width: 120,
          headerName: this.localizationService.instant('Doc::SupplierDeliveryNote:Discount'),
          type: FieldTypes.String,
          suppressNavigable: true,

          editable: params => this.isCellEditable(params),
        },
        {
          field: 'additionalPercentage',
          width: 120,
          headerName: this.localizationService.instant(
            'Doc::SupplierDeliveryNote:AdditionalPercentage',
          ),
          type: FieldTypes.String,
          suppressNavigable: true,

          editable: params => this.isCellEditable(params),
        },
        {
          field: 'additionalCostPrice',
          width: 120,
          headerName: this.localizationService.instant('Doc::SupplierDeliveryNote:AdditionalCost'),
          type: FieldTypes.String,
          suppressNavigable: true,

          editable: params => this.isCellEditable(params),
        },
        {
          field: 'sourceLineDisplay',
          pinned: 'left',
          width: 180,
          type: FieldTypes.String,
          headerName: this.localizationService.instant(
            'Doc::SupplierDeliveryNote:SalesPurchaseLineReference',
          ),
          editable: params =>
            params.data.lineRole == this.articleLineRole &&
            (params.data.manualSourceLineId != null || params.data.sourceLineId == null) &&
            this.isCellEditable(params),
          cellEditorSelector: ({ data }: { data }) => {
            if (data?.lineRole == this.articleLineRole) {
              return {
                component: TypeAheadCellEditorComponent<BaseDocDto, SupplierPurchaseOrderLookup>,
                params: {
                  minLengthTerm: 0,
                  lookupFn: term =>
                    this.service.getSupplierPurchaseOrderLookups(
                      this._document.thirdPartyId,
                      data.articleId,
                      this.isSerialEntry(),
                      term,
                      this.selectedPurchaseOrderSelectedIds(),
                    ),
                  formatFn: item => (typeof item === 'string' ? item : item.label),
                  callbackFn: ({
                    line,
                    selectedItem,
                  }: {
                    line: any;
                    selectedItem: SupplierPurchaseOrderLookup;
                  }) => {
                    this._updateQueue
                      .enqueue(() =>
                        this.service.setLinePurchaseArticleLineId(
                          this._document.id,
                          this._document.concurrencyStamp,
                          line.id,
                          selectedItem.id,
                        ),
                      )
                      .subscribe(response => this._updateLines(response));
                  },
                } as ITypeAheadCellEditorParams<BaseDocDto, SupplierPurchaseOrderLookup>,
              };
            } else {
              return { component: 'agTextCellEditor' };
            }
          },
        },
      ] as Array<ColDef>),
    );

    this.columnDefs = this.policy.enhanceColDefs(columnDefs);
  }

  isChapter = line => line.lineRole == DocLineRole.DocChapter;

  public override isCellEditable(params: EditableCallbackParams<any, any>) {
    let isEditable = true;

    if (params.data.lineRole == this.articleLineRole) {
      if (params.colDef.field != 'code') {
        isEditable = params.data.articleId != null;
      }
      if (params.data.sourceLineId != null) {
        switch (params.colDef.field) {
          case 'description':
          case 'articleSalesUnitCode':
          case 'orderedQuantity':
          case 'remainingQuantityToReceived':
          case 'totalReceivedQuantity':
          case 'orderedQuantityInStockUnit':
          case 'remainingQuantityToReceivedInStockUnit':
          case 'totalReceivedQuantityInStockUnit':
          case 'purchaseArticleLineId':
            isEditable = false;
            break;
          case 'sourceLineDisplay':
            isEditable = params.data.manualSourceLineId;
            break;
        }
      } else {
        switch (params.colDef.field) {
          case 'description':
          case 'remainingQuantityToReceived':
          case 'totalReceivedQuantity':
          case 'remainingQuantityToReceivedInStockUnit':
          case 'totalReceivedQuantityInStockUnit':
            isEditable = false;
            break;
        }
      }
      switch (params.colDef.field) {
        case 'quantity':
        case 'quantityInStockUnit':
          isEditable = super.isCellEditable(params) && !params.data.articleUsesSerial;
          break;
        case 'orderedQuantity':
        case 'orderedQuantityInStockUnit':
          isEditable = false;
          break;
      }
      if (
        (this.document as SupplierDeliveryNoteDto).deliveryStatus ==
        SupplierDeliveryStatus.CheckValuation
      ) {
        const editableFields = ['unitPrice', 'discount'];
        isEditable = editableFields.includes(params.colDef.field);
      }
    }

    return isEditable && super.isCellEditable(params);
  }

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    if (this._readonly) return [];

    return this.contextMenuItems(params);
  };

  protected getCurrentCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<DocLineUpdatedDto | void> {
    let observable: Observable<DocLineUpdatedDto | void> = super.getCellValueChangedObservable(
      event,
    );
    if (observable == EMPTY) {
      switch (event.column.getColId()) {
        case 'unitPrice':
          observable = this.service.setLineUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitPrice,
            true,
          );
          break;
        case 'articleStockUnitPrice':
          observable = this.service.setLineStockUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleStockUnitPrice,
            true,
          );
          break;
        case 'discount':
          observable = this.service.setLineDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.discount,
          );
          break;
        case 'unitWeight':
          observable = this.service.setLineUnitWeight(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitWeight,
          );
          break;
        case 'supplierDiscount':
          observable = this.service.setLineSupplierDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.supplierDiscount,
          );
          break;
        case 'additionalPercentage':
          observable = this.service.setLineAdditionalPercentage(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.additionalPercentage,
          );
          break;
        case 'additionalCostPrice':
          observable = this.service.setLineAdditionalCostPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.additionalCostPrice,
          );
          break;
        case 'articleSalesUnitCode':
          observable = this.service.setLineArticleSalesUnitId(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleSalesUnitCode,
          );
          break;
        default:
          observable = EMPTY;
          break;
      }
    }
    return observable;
  }

  protected override getCellValueChangedObservable(e: CellValueChangedEvent): Observable<any> {
    const fromPolicy = this.policy.onCellValueChanged(e);
    let observable: Observable<any>;
    if (fromPolicy === true) {
      observable = this.getCurrentCellValueChangedObservable(e);
    } else {
      observable = fromPolicy instanceof Observable ? fromPolicy : of(fromPolicy);
    }
    this.policy.beforeSaveLine();
    return observable.pipe(
      map((response: DocLineUpdatedDto) => this.policy.onUnsavedLineChanged(response)),
    );
  }

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    let obs: Observable<DocLineUpdatedDto> = this.getCellValueChangedObservable(event);

    obs.pipe(first()).subscribe({
      next: response => {
        if (response) this._updateLines(response);
      },
      error: _ => {
        let oldData = event.data;
        oldData[event.colDef.field] = event.oldValue;
        this.lineGrid.grid.grid.api.applyTransaction({
          update: [oldData],
        });
      },
    });
  };

  protected stockLocationFormatter(data) {
    if (data == null || data.stockLocation == null) return '';

    if (typeof data.stockLocation === 'string')
      return data.stockLocation != 'stockLocation' ? data.stockLocation : '';

    return data.stockLocation.code ?? '';
  }

  protected override addArticleLine(lineFromId?: string, reference?: number): void {
    if (this.lineGrid.grid.rowData.length == 0) {
      super.addArticleLine(lineFromId, reference);
      return;
    }

    this.policy.beforeAddArticleLine().subscribeOnce(res => {
      if (res) {
        this.updateLines(res);
      }

      super.addArticleLine(lineFromId, reference);
    });
  }

  public beforeSaveDocument(): Observable<DocLineUpdatedDto> {
    return this.policy.beforeSaveDoc();
  }

  protected override onLineAdded(line: DocLineUpdatedDto, setFocus: boolean = true): void {
    super.onLineAdded(line, this.policy.focusOnNewLine());
    this.policy.onLineAdded();
  }
}
